import {
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
  AlignStartHorizontal,
  AlignCenterHorizontal,
  AlignEndHorizontal,
  Columns3,
  Rows3,
  Group,
  Ungroup,
} from 'lucide-react';
import {
  alignLeft,
  alignCenterH,
  alignRight,
  alignTop,
  alignMiddleV,
  alignBottom,
  alignAllToArtboard,
  distributeHorizontally,
  distributeVertically,
  distributeEdgesLeft,
  distributeEdgesCenterH,
  distributeEdgesRight,
  distributeEdgesTop,
  distributeEdgesMiddleV,
  distributeEdgesBottom,
} from '../../lib/alignmentUtils';
import { useCanvasStore } from '../../stores/canvasStore';
import type { CanvasElement } from '../../types';

interface AlignmentToolbarProps {
  selectedElements: CanvasElement[];
  updateElements: (updates: Array<{ id: string; changes: Partial<CanvasElement> }>) => void;
  onGroup: () => void;
  onUngroup: () => void;
  isGroupSelected: boolean;
  artboardWidth: number;
  artboardHeight: number;
}

interface AlignableBox {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface PositionUpdate {
  id: string;
  x?: number;
  y?: number;
}

type AlignDirection = 'left' | 'centerH' | 'right' | 'top' | 'centerV' | 'bottom';
type AlignTo = 'selection' | 'artboard';

const ALIGN_TO_OPTIONS: { value: AlignTo; label: string }[] = [
  { value: 'selection', label: 'Selection' },
  { value: 'artboard', label: 'Artboard' },
];

function ToolbarButton({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`p-1.5 rounded transition-colors ${
        disabled
          ? 'text-white/15 cursor-not-allowed'
          : 'text-white/50 hover:text-cyan-400 hover:bg-white/[0.06]'
      }`}
    >
      {icon}
    </button>
  );
}

function ToolbarDivider() {
  return <div className="w-px h-5 bg-white/[0.06] mx-1" />;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[10px] font-medium text-white/40 uppercase tracking-wider mr-2 select-none">
      {children}
    </span>
  );
}

export default function AlignmentToolbar({
  selectedElements,
  updateElements,
  onGroup,
  onUngroup,
  isGroupSelected,
  artboardWidth,
  artboardHeight,
}: AlignmentToolbarProps) {
  const alignTo = useCanvasStore((s) => s.alignTo);
  const setAlignTo = useCanvasStore((s) => s.setAlignTo);

  const count = selectedElements.length;
  const hasMultiple = count >= 2;
  const hasThreeOrMore = count >= 3;
  const canAlign = alignTo === 'selection' ? hasMultiple : count >= 1;

  const getBoxes = (): AlignableBox[] =>
    selectedElements
      .filter((el) => !el.locked)
      .map((el) => ({ id: el.id, x: el.x, y: el.y, width: el.width, height: el.height }));

  const applyUpdates = (updates: PositionUpdate[]) => {
    const batch = updates
      .filter((u) => u.x !== undefined || u.y !== undefined)
      .map((u) => ({
        id: u.id,
        changes: {
          ...(u.x !== undefined ? { x: u.x } : {}),
          ...(u.y !== undefined ? { y: u.y } : {}),
        } as Partial<CanvasElement>,
      }));
    if (batch.length > 0) updateElements(batch);
  };

  const handleAlign = (
    selectionFn: (els: AlignableBox[]) => PositionUpdate[],
    direction: AlignDirection
  ) => {
    if (alignTo === 'selection') {
      applyUpdates(selectionFn(getBoxes()));
    } else {
      applyUpdates(alignAllToArtboard(getBoxes(), direction, artboardWidth, artboardHeight));
    }
  };

  const handleDistribute = (fn: (els: AlignableBox[]) => PositionUpdate[]) => {
    applyUpdates(fn(getBoxes()));
  };

  return (
    <div className="flex flex-wrap items-center gap-y-2 gap-1 px-3 py-2 bg-[#0a0a0f]/95 backdrop-blur-md border-b border-white/[0.12] shrink-0">
      <div className="flex items-center gap-2 mr-2">
        <span className="text-[10px] font-medium text-white/40 uppercase tracking-wider select-none">
          Align to
        </span>
        <div className="flex gap-1 p-1 rounded-lg bg-[#12121a]">
          {ALIGN_TO_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              aria-pressed={alignTo === opt.value}
              onClick={() => setAlignTo(opt.value)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                alignTo === opt.value
                  ? 'bg-cyan-500/20 text-cyan-400'
                  : 'text-white/40 hover:text-white/60'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <ToolbarDivider />

      <SectionLabel>Align</SectionLabel>
      <ToolbarButton
        icon={<AlignStartVertical size={20} />}
        label="Align left (Ctrl+Shift+L)"
        onClick={() => handleAlign(alignLeft, 'left')}
        disabled={!canAlign}
      />
      <ToolbarButton
        icon={<AlignCenterVertical size={20} />}
        label="Align center (Ctrl+Shift+C)"
        onClick={() => handleAlign(alignCenterH, 'centerH')}
        disabled={!canAlign}
      />
      <ToolbarButton
        icon={<AlignEndVertical size={20} />}
        label="Align right (Ctrl+Shift+R)"
        onClick={() => handleAlign(alignRight, 'right')}
        disabled={!canAlign}
      />

      <ToolbarDivider />

      <ToolbarButton
        icon={<AlignStartHorizontal size={20} />}
        label="Align top (Ctrl+Shift+T)"
        onClick={() => handleAlign(alignTop, 'top')}
        disabled={!canAlign}
      />
      <ToolbarButton
        icon={<AlignCenterHorizontal size={20} />}
        label="Align middle (Ctrl+Shift+M)"
        onClick={() => handleAlign(alignMiddleV, 'centerV')}
        disabled={!canAlign}
      />
      <ToolbarButton
        icon={<AlignEndHorizontal size={20} />}
        label="Align bottom (Ctrl+Shift+B)"
        onClick={() => handleAlign(alignBottom, 'bottom')}
        disabled={!canAlign}
      />

      <ToolbarDivider />

      <SectionLabel>Distribute Objects</SectionLabel>
      <ToolbarButton
        icon={<AlignStartVertical size={20} />}
        label="Distribute left edges"
        onClick={() => handleDistribute(distributeEdgesLeft)}
        disabled={!hasThreeOrMore}
      />
      <ToolbarButton
        icon={<AlignCenterVertical size={20} />}
        label="Distribute horizontal centers"
        onClick={() => handleDistribute(distributeEdgesCenterH)}
        disabled={!hasThreeOrMore}
      />
      <ToolbarButton
        icon={<AlignEndVertical size={20} />}
        label="Distribute right edges"
        onClick={() => handleDistribute(distributeEdgesRight)}
        disabled={!hasThreeOrMore}
      />
      <ToolbarButton
        icon={<AlignStartHorizontal size={20} />}
        label="Distribute top edges"
        onClick={() => handleDistribute(distributeEdgesTop)}
        disabled={!hasThreeOrMore}
      />
      <ToolbarButton
        icon={<AlignCenterHorizontal size={20} />}
        label="Distribute vertical centers"
        onClick={() => handleDistribute(distributeEdgesMiddleV)}
        disabled={!hasThreeOrMore}
      />
      <ToolbarButton
        icon={<AlignEndHorizontal size={20} />}
        label="Distribute bottom edges"
        onClick={() => handleDistribute(distributeEdgesBottom)}
        disabled={!hasThreeOrMore}
      />

      <ToolbarDivider />

      <SectionLabel>Distribute Spacing</SectionLabel>
      <ToolbarButton
        icon={<Columns3 size={20} />}
        label="Space horizontally (equal gaps)"
        onClick={() => handleDistribute(distributeHorizontally)}
        disabled={!hasThreeOrMore}
      />
      <ToolbarButton
        icon={<Rows3 size={20} />}
        label="Space vertically (equal gaps)"
        onClick={() => handleDistribute(distributeVertically)}
        disabled={!hasThreeOrMore}
      />

      <ToolbarDivider />

      <SectionLabel>Group</SectionLabel>
      <ToolbarButton
        icon={<Group size={20} />}
        label="Group (Ctrl+G)"
        onClick={onGroup}
        disabled={!hasMultiple}
      />
      <ToolbarButton
        icon={<Ungroup size={20} />}
        label="Ungroup (Ctrl+Shift+G)"
        onClick={onUngroup}
        disabled={!isGroupSelected}
      />

      <div className="ml-auto">
        <span className="text-[10px] text-white/30">
          {count} selected
        </span>
      </div>
    </div>
  );
}
